import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Inbox, Mail, Phone, Building2, CalendarDays, Trash2, Save } from "lucide-react";

const TYPE_LABEL = { sponsor: "Sponsor a Friday", guest: "Be a guest", both: "Sponsor & guest", general: "General" };
const FRIDAY_LABEL = { 1: "1st \u00b7 Arts & Entertainment", 2: "2nd \u00b7 Generosity", 3: "3rd \u00b7 Youth & Next Gen", 4: "4th \u00b7 Intentionality" };
const STATUS_STYLE = {
  new: "border border-[rgba(201,119,46,0.45)] bg-[rgba(201,119,46,0.18)] text-[#8A4A18]",
  contacted: "",
  closed: "",
};

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(async () => {
    const params = filter === "all" ? {} : { status: filter };
    const r = await api.get("/inquiries", { params }).then((x) => x.data).catch(() => []);
    setItems(r);
    setNotesDraft(Object.fromEntries(r.map((i) => [i.inquiry_id, i.notes || ""])));
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (inq, status) => {
    try { await api.patch(`/inquiries/${inq.inquiry_id}`, { status }); toast.success(`Marked ${status}`); await load(); }
    catch (e) { toast.error("Could not update status"); }
  };
  const saveNotes = async (inq) => {
    try { await api.patch(`/inquiries/${inq.inquiry_id}`, { notes: notesDraft[inq.inquiry_id] || "" }); toast.success("Notes saved"); }
    catch (e) { toast.error("Could not save notes"); }
  };
  const remove = async (inq) => {
    try { await api.delete(`/inquiries/${inq.inquiry_id}`); toast.success("Inquiry deleted"); await load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Inquiries</h1>
        <p className="mt-1 text-muted-foreground">Sponsor and guest inquiries from the Partner page.</p>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="admin-inquiries-filter">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="new">New</TabsTrigger>
          <TabsTrigger value="contacted">Contacted</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <SectionLoader />
      ) : items.length === 0 ? (
        <EmptyState icon={Inbox} title="No inquiries yet" description="Sponsor and guest inquiries will show up here." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {items.map((inq) => (
            <Card key={inq.inquiry_id} className="border-border bg-card" data-testid="admin-inquiry-card">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-foreground">{inq.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                      <a href={`mailto:${inq.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3.5 w-3.5" />{inq.email}</a>
                      {inq.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{inq.phone}</span>}
                      {inq.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{inq.company}</span>}
                    </div>
                  </div>
                  <Badge className={STATUS_STYLE[inq.status] || ""} variant={inq.status === "new" ? "default" : "secondary"}>{inq.status}</Badge>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{TYPE_LABEL[inq.inquiry_type] || "General"}</Badge>
                  {inq.preferred_friday && <Badge variant="outline">{FRIDAY_LABEL[inq.preferred_friday]}</Badge>}
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" />{formatDate(inq.created_at)}</span>
                </div>

                <p className="mt-3 whitespace-pre-line rounded-lg bg-secondary/50 p-3 text-sm text-foreground">{inq.message}</p>

                <div className="mt-4">
                  <Textarea
                    rows={2}
                    placeholder="Internal notes…"
                    value={notesDraft[inq.inquiry_id] ?? ""}
                    onChange={(e) => setNotesDraft({ ...notesDraft, [inq.inquiry_id]: e.target.value })}
                    data-testid="admin-inquiry-notes"
                  />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Select value={inq.status} onValueChange={(v) => setStatus(inq, v)}>
                    <SelectTrigger className="h-9 w-[150px]" data-testid="admin-inquiry-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="secondary" onClick={() => saveNotes(inq)} data-testid="admin-inquiry-save-notes"><Save className="mr-1.5 h-3.5 w-3.5" />Save notes</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="ml-auto text-destructive"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete this inquiry?</AlertDialogTitle><AlertDialogDescription>This permanently removes the inquiry from {inq.name}.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(inq)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
